import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Button, 
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Tooltip
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaTrash } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, ControlsContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import { 
  MainContainer, 
  SkillsGrid, 
  SkillCard, 
  SkillCardContent, 
  SkillTitle, 
  EmployeeBadge,
  ActionButtons,
  UsageBadgesContainer,
  UsageBadge,
  CreatedAtText
} from './styles';
import { COLORS } from '../../../constants/radar';
import { useAlert } from '../../../contexts/AlertContext';
import { cvManagementApi } from '../../../services/api';
import SkillsItems from '../../MyActivities/MyCV/components/common/SkillsItems';
import { getRelativeTime } from '../../../utils/dateUtils';
import LoadingSpinner from '../../common/LoadingSpinner';

const SkillsReconciliation = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [customSkills, setCustomSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentSkill, setCurrentSkill] = useState({ id: null, name: '' });
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    skill: null,
    deleting: false
  });

  // Load skills on component mount
  useEffect(() => {
    fetchCustomSkills();
  }, []);

  const fetchCustomSkills = async () => {
    try {
      setLoading(true);
      const response = await cvManagementApi.getCustomSkills();
      setCustomSkills(response || []);
    } catch (error) {
      console.error('Error fetching custom skills:', error);
      showAlert('Failed to load custom skills', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (skill = null) => {
    if (skill) {
      setCurrentSkill({
        id: skill.id,
        name: skill.name
      });
      setSelectedSkills([]);
    } else {
      setCurrentSkill({ id: null, name: '' });
      setSelectedSkills([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSkills([]);
    setExpandedIndex(null);
  };

  const handleSkillsChange = (newSkills) => {
    setSelectedSkills(newSkills);
  };

  const handleReconcileSkill = async () => {
    if (selectedSkills.length === 0) {
      showAlert('Please select at least one existing skill to reconcile with', 'error');
      return;
    }

    try {
      // Call the API to reconcile the custom skill with the selected skills
      await cvManagementApi.reconcileSkill(
        currentSkill.id,
        selectedSkills[0].id
      );

      showAlert('Skill reconciled successfully', 'success');

      // Refresh skills list
      await fetchCustomSkills();
      handleCloseDialog();
    } catch (error) {
      console.error('Error reconciling skill:', error);
      showAlert(`Failed to reconcile skill: ${error.message}`, 'error');
    }
  };

  const handleDeleteClick = (skill) => {
    setDeleteModal({
      open: true,
      skill,
      deleting: false
    });
  };

  const handleDelete = async () => {
    if (!deleteModal.skill) return;

    try {
      setDeleteModal(prev => ({ ...prev, deleting: true }));

      await cvManagementApi.deleteCustomSkill(deleteModal.skill.id);

      showAlert('Custom skill deleted successfully', 'success');

      // Refresh skills list
      await fetchCustomSkills();
    } catch (error) {
      console.error('Error deleting custom skill:', error);
      showAlert(error.message || 'Failed to delete custom skill', 'error');
    } finally {
      // Close the modal
      setDeleteModal({ open: false, skill: null, deleting: false });
    }
  };

  // Filter skills based on search term
  const filteredSkills = customSkills.filter(skill =>
    skill.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    (skill.employee && skill.employee.fullName.toLowerCase().includes((searchTerm || '').toLowerCase()))
  );

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Skills Reconciliation</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Skills"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>
          </Box>
        </ControlsContainer>

        <MainContainer>
          {!loading && (
            <div style={{
              color: '#64748B',
              fontSize: '14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontWeight: 600 }}>
                {filteredSkills.length}
              </span>
              {filteredSkills.length === 1 ? 'skill' : 'skills'} found
            </div>
          )}

          {loading ? (
            <LoadingSpinner message="Loading skills..." />
          ) : filteredSkills.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px dashed #dee2e6'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#6c757d',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                No skills found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#adb5bd',
                  maxWidth: '400px'
                }}
              >
                {searchTerm ? 
                  "No skills match your search criteria. Try adjusting your search term." : 
                  "No custom skills have been added yet."}
              </Typography>
            </Box>
          ) : (
            <SkillsGrid>
              {filteredSkills.map((skill) => (
                <SkillCard key={skill.id}>
                  <SkillCardContent>
                    <SkillTitle>
                      {skill.name}
                      <UsageBadgesContainer>
                        {skill.isUsedInGeneralSkills && (
                          <UsageBadge>
                            <SchoolIcon /> General Skills
                          </UsageBadge>
                        )}
                        {skill.isUsedInEmploymentHistory && (
                          <UsageBadge>
                            <WorkIcon /> Employment History
                          </UsageBadge>
                        )}
                      </UsageBadgesContainer>
                    </SkillTitle>
                    
                    {(skill.employee || skill.employeeCV) && (
                      <EmployeeBadge>
                        <PersonIcon fontSize="small" /> {skill.employee ? skill.employee.fullName : skill.employeeCV.fullName}
                      </EmployeeBadge>
                    )}

                    {skill.segment && (
                      <CreatedAtText>
                        <CategoryIcon fontSize="small" /> Part of <span style={{ fontWeight: 600, color: COLORS.PRIMARY }}>{skill.segment.name}</span>
                      </CreatedAtText>
                    )}

                    {skill.createdAt && (
                      <CreatedAtText>
                        <AccessTimeIcon /> Added {getRelativeTime(skill.createdAt)}
                      </CreatedAtText>
                    )}

                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(skill)}
                        title="Reconcile with existing skills"
                        color="primary"
                      >
                        <LinkIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(skill)}
                        title="Delete custom skill"
                        color="error"
                      >
                        <FaTrash />
                      </IconButton>
                    </ActionButtons>
                  </SkillCardContent>
                </SkillCard>
              ))}
            </SkillsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Reconcile Skill Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: { xs: '95%', sm: '80%', md: '800px' },
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle>Reconcile Custom Skill</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Reconcile the custom skill <strong>"{currentSkill.name}"</strong> with existing skills in the radar.
          </DialogContentText>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }}>
              Select a Skill to Reconcile With
            </Typography>
            <SkillsItems
              skills={selectedSkills}
              onSkillsChange={handleSkillsChange}
              expandedIndex={expandedIndex}
              setExpandedIndex={setExpandedIndex}
              globalSearch={true}
              hideLevels={true}
              addButtonText="Add a standard skill"
              emptyStateTitle="No standard skills selected"
              emptyStateDescription="Select a standard skill to reconcile with the custom skill."
              singleSelection={true}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleReconcileSkill} 
            variant="contained"
            startIcon={<LinkIcon />}
            disabled={!selectedSkills[0]?.id}
            sx={{ 
              bgcolor: COLORS.PRIMARY,
              '&:hover': {
                bgcolor: COLORS.PRIMARY_DARK,
              }
            }}
          >
            Reconcile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, skill: null, deleting: false })}
        onConfirm={handleDelete}
        title="Delete Custom Skill"
        message={
          deleteModal.skill
            ? `Are you sure you want to delete the custom skill "${deleteModal.skill.name}"? This action cannot be undone and will remove all references to this skill.`
            : ""
        }
        loading={deleteModal.deleting}
      />
    </Container>
  );
};

export default SkillsReconciliation; 